import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Phone, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const RESEND_COOLDOWN = 60;

export default function PhoneVerificationModal({ open, phone, onVerified, onClose }) {
  const { api } = useAuth();
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const phoneLast4 = phone ? phone.replace(/\D/g, '').slice(-4) : '????';

  const sendCode = async () => {
    if (!phone) return;
    setSending(true);
    try {
      await api.post('/auth/phone/send-code/', { phone, purpose: 'checkout' });
      setSent(true);
      setCooldown(RESEND_COOLDOWN);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send verification code.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Send code automatically when modal opens
  useEffect(() => {
    if (open && phone) {
      setCode('');
      setSent(false);
      setVerified(false);
      setCooldown(0);
      sendCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code.');
      return;
    }
    setVerifying(true);
    try {
      await api.post('/auth/phone/verify-code/', { phone, code, purpose: 'checkout' });
      setVerified(true);
      setTimeout(() => {
        onVerified();
      }, 800);
    } catch (err) {
      const msg = err.response?.data?.error || 'Incorrect code. Please try again.';
      toast.error(msg);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center">
              <Phone className="h-5 w-5 text-blue-600" />
            </div>
            <DialogTitle className="text-lg">Verify your phone number</DialogTitle>
          </div>
          <DialogDescription>
            We sent a 6-digit code to the number ending in <strong>...{phoneLast4}</strong>.
            Enter it below to confirm your number and proceed to payment.
          </DialogDescription>
        </DialogHeader>

        {verified ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <CheckCircle className="h-12 w-12 text-emerald-500" />
            <p className="font-medium text-emerald-700">Phone verified! Redirecting to payment…</p>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div>
              <Input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={handleKeyDown}
                className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                disabled={verifying || !sent}
                data-testid="phone-verify-code-input"
              />
            </div>

            <Button
              className="btn-primary w-full"
              onClick={handleVerify}
              disabled={verifying || code.length !== 6 || !sent}
              data-testid="phone-verify-submit"
            >
              {verifying ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying…</>
              ) : (
                'Verify & Continue'
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              {sending ? (
                <span className="flex items-center justify-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Sending code…
                </span>
              ) : cooldown > 0 ? (
                <span>Resend code in {cooldown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={sendCode}
                  className="text-primary underline underline-offset-2 hover:no-underline"
                >
                  Resend code
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
